import React from "react";
import {
  Box,
  Grid,
  Typography,
  MenuItem,
  FormControl,
  CircularProgress,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  GoogleMap,
  Marker,
  DirectionsRenderer,
  OverlayView,
} from "@react-google-maps/api";
import DeleteModal from "../common/DeleteModal";
import {
  getOriginStopIcon,
  getDestinationStopIcon,
  getIntermediateStopIcon,
} from "./icons";
import {
  FormContainer,
  MapContainer,
  StyledTextField,
  StyledSelect,
  ConfirmButton,
  CancelButton,
  DeleteButton,
  ImageUploadBox,
  ErrorText,
  fullContainerStyle,
} from "./CreateStudentForm.styles";
import {
  BANGALORE_AIRPORT,
  REQUIRED_ASTERISK_SX,
  formatVehicleOptionLabel,
} from "./CreateStudentForm.helpers";
import { getPlaceConfig } from "../dashboard/functions.js";
import { useTranslation } from "react-i18next";

const labelSx = {
  my: 1,
  fontWeight: 600,
  fontSize: "20px",
  color: "#1F242E",
};

export default function CreateStudentFormView(props) {
  const { t } = useTranslation();
  const {
    isEdit,
    onClose,
    onLoad,
    onUnmount,
    handleImageUpload,
    handleChange,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
    handleSubmit,
    onDelete,
    showDeleteModal,
    imageError,
    submitError,
    imagePreview,
    formData,
    errors,
    isStopDetailsLoading,
    routes,
    filteredVehicles,
    availablePickupStops,
    availableDropStops,
    directions,
    selectedRouteColor,
    stopsToDisplay,
    handleProfileImageDelete,
    handleParentView,
    getVehicleSeatCapacityError,
    placeMarkers,
    getMapOptions,
    nfcCards = [],
  } = props;

  return (
    <FormContainer>
      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <input
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                style={{ display: "none" }}
                id={`image-upload-${isEdit ? "edit" : "create"}`}
                type="file"
                onChange={handleImageUpload}
              />

              <Box sx={{ position: "relative", width: "180px" }}>
                <label htmlFor={`image-upload-${isEdit ? "edit" : "create"}`}>
                  <ImageUploadBox
                    sx={{
                      border: imageError
                        ? "2px dashed #cc0000"
                        : "2px dashed #183059",
                    }}
                  >
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 32 32"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M11.9993 17.3333L15.9993 12L19.9993 18V16H25.3327V6.66667C25.3327 5.196 24.1367 4 22.666 4H5.33268C3.86202 4 2.66602 5.196 2.66602 6.66667V22.6667C2.66602 24.1373 3.86202 25.3333 5.33268 25.3333H15.9993V20H6.66602L10.666 14.6667L11.9993 17.3333Z"
                          fill={imageError ? "#cc0000" : "#20A8E0"}
                        />
                        <path
                          d="M25.3327 18.667H22.666V22.667H18.666V25.3337H22.666V29.3337H25.3327V25.3337H29.3327V22.667H25.3327V18.667Z"
                          fill={imageError ? "#cc0000" : "#20A8E0"}
                        />
                      </svg>
                    )}
                  </ImageUploadBox>
                </label>
              </Box>

              {imageError && <ErrorText>{imageError}</ErrorText>}
              {imagePreview && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 4,
                    zIndex: 2,
                    // backgroundColor: "black",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: 1,
                  }}
                >
                  {/* Edit Icon */}
                  <label
                    htmlFor={`image-upload-${isEdit ? "edit" : "create"}`}
                    style={{ cursor: "pointer" }}
                  >
                    <svg
                      width="52"
                      height="36"
                      viewBox="0 0 52 36"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="0.5"
                        y="0.5"
                        width="51"
                        height="35"
                        rx="7.5"
                        fill="#F2F2F3"
                      />
                      <rect
                        x="0.5"
                        y="0.5"
                        width="51"
                        height="35"
                        rx="7.5"
                        stroke="#20A8E0"
                      />
                      <path
                        d="M29.333 9.67676L31.833 12.1768L29.9272 14.0834L27.4272 11.5834L29.333 9.67676ZM19.333 19.6668V22.1668H21.833L28.7488 15.2609L26.2488 12.7609L19.333 19.6668ZM19.333 24.6668H32.6663V26.3334H19.333V24.6668Z"
                        fill="#20A8E0"
                      />
                    </svg>
                  </label>

                  {/* Delete Icon */}
                  <Box
                    component="span"
                    onClick={handleProfileImageDelete}
                    sx={{ cursor: "pointer" }}
                  >
                    <svg
                      width="52"
                      height="36"
                      viewBox="0 0 52 36"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="0.5"
                        y="0.5"
                        width="51"
                        height="35"
                        rx="7.5"
                        fill="#F2F2F3"
                      />
                      <rect
                        x="0.5"
                        y="0.5"
                        width="51"
                        height="35"
                        rx="7.5"
                        stroke="#EE3B2B"
                      />
                      <path
                        d="M21 13.8337H20.1667V24.667C20.1667 25.109 20.3423 25.5329 20.6548 25.8455C20.9674 26.1581 21.3913 26.3337 21.8333 26.3337H30.1667C30.6087 26.3337 31.0326 26.1581 31.3452 25.8455C31.6577 25.5329 31.8333 25.109 31.8333 24.667V13.8337H21ZM24.3333 23.8337H22.6667V16.3337H24.3333V23.8337ZM29.3333 23.8337H27.6667V16.3337H29.3333V23.8337ZM29.8483 11.3337L28.5 9.66699H23.5L22.1517 11.3337H18.5V13.0003H33.5V11.3337H29.8483Z"
                        fill="#EE3B2B"
                      />
                    </svg>
                  </Box>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={labelSx}>
                {t("Full Name")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.fullName}
                onChange={handleChange("fullName")}
                placeholder={t("Full Name")}
                error={!!errors.fullName}
                data-field="fullName"
              />
              {errors.fullName && <ErrorText>{errors.fullName}</ErrorText>}
              <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={labelSx}>
                    {t("Grade")}
                    <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                      *
                    </Box>
                  </Typography>
                  <StyledTextField
                    fullWidth
                    value={formData.grade}
                    onChange={handleChange("grade")}
                    placeholder={t("Grade")}
                    error={!!errors.grade}
                    data-field="grade"
                  />
                  {errors.grade && <ErrorText>{errors.grade}</ErrorText>}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={labelSx}>
                    {t("Sec")}
                    <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                      *
                    </Box>
                  </Typography>
                  <StyledTextField
                    fullWidth
                    value={formData.sec}
                    onChange={handleChange("sec")}
                    placeholder={t("Sec")}
                    error={!!errors.sec}
                    data-field="sec"
                  />
                  {errors.sec && <ErrorText>{errors.sec}</ErrorText>}
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={labelSx}>
                {t("Student ID")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.studentId}
                onChange={handleChange("studentId")}
                placeholder={t("Student ID")}
                error={!!errors.studentId}
                data-field="studentId"
              />
              {errors.studentId && <ErrorText>{errors.studentId}</ErrorText>}
              <Typography variant="body2" sx={labelSx}>
                {t("Class Teacher")}
                <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                  *
                </Box>
              </Typography>
              <StyledTextField
                fullWidth
                value={formData.classTeacher}
                onChange={handleChange("classTeacher")}
                placeholder={t("Class Teacher")}
                error={!!errors.classTeacher}
                data-field="classTeacher"
              />
              {errors.classTeacher && (
                <ErrorText>{errors.classTeacher}</ErrorText>
              )}
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={labelSx}>
                    {t("Parent ID")}
                    <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                      *
                    </Box>
                  </Typography>
                  <StyledTextField
                    fullWidth
                    value={formData.parentId}
                    onChange={handleChange("parentId")}
                    placeholder={t("Parent ID")}
                    error={!!errors.parentId}
                    data-field="parentId"
                  />
                  {errors.parentId && <ErrorText>{errors.parentId}</ErrorText>}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={labelSx}>
                    {t("Parent Name")}
                    <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                      *
                    </Box>
                  </Typography>
                  <StyledTextField
                    fullWidth
                    value={formData.parentName}
                    onChange={handleChange("parentName")}
                    placeholder={t("Parent Name")}
                    error={!!errors.parentName}
                    data-field="parentName"
                  />
                  {errors.parentName && (
                    <ErrorText>{errors.parentName}</ErrorText>
                  )}
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" sx={labelSx}>
                    {t("Parent's Phone Number")}
                    <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                      *
                    </Box>
                  </Typography>
                  <StyledTextField
                    fullWidth
                    value={formData.parentPhone}
                    onChange={handleChange("parentPhone")}
                    placeholder={t("Parent's Phone Number")}
                    error={!!errors.parentPhone}
                    data-field="parentPhone"
                  />
                  {errors.parentPhone && (
                    <ErrorText>{errors.parentPhone}</ErrorText>
                  )}
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" sx={labelSx}>
                    {t("Route")}
                    <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                      *
                    </Box>
                  </Typography>
                  <FormControl fullWidth>
                    <StyledSelect
                      value={formData.routeId}
                      onChange={handleChange("routeId")}
                      displayEmpty
                      error={!!errors.routeId}
                      data-field="routeId"
                      disabled={isStopDetailsLoading}
                    >
                      <MenuItem value="" disabled>
                        {t("Select Route")}
                      </MenuItem>
                      {routes.map((route) => (
                        <MenuItem key={route.id} value={route.id}>
                          {route.name}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </FormControl>
                  {errors.routeId && <ErrorText>{errors.routeId}</ErrorText>}
                  {isStopDetailsLoading && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      mt={1}
                      ml={1}
                    >
                      {/* <CircularProgress size={1} thickness={1} /> */}
                      <Typography variant="caption" sx={{ color: "#5F6B7A" }}>
                        {t("Loading route stops...")}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" sx={labelSx}>
                    {t("Assigned Vehicle")}
                    <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                      *
                    </Box>
                  </Typography>
                  <FormControl fullWidth>
                    <StyledSelect
                      value={formData.vehicleId}
                      onChange={handleChange("vehicleId")}
                      displayEmpty
                      disabled={!formData.routeId || isStopDetailsLoading}
                      error={!!errors.vehicleId}
                      data-field="vehicleId"
                    >
                      <MenuItem value="" disabled>
                        {t("Select Vehicle")}
                      </MenuItem>
                      {filteredVehicles.map((vehicle) => (
                        <MenuItem
                          key={vehicle.id}
                          value={vehicle.id}
                          disabled={!!getVehicleSeatCapacityError?.(vehicle.id)}
                        >
                          {formatVehicleOptionLabel(vehicle)}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </FormControl>
                  {errors.vehicleId && (
                    <ErrorText>{errors.vehicleId}</ErrorText>
                  )}
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" sx={labelSx}>
                    {t("Pickup Point")}
                    <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                      *
                    </Box>
                  </Typography>
                  <FormControl fullWidth>
                    <StyledSelect
                      value={formData.pickupStopId}
                      onChange={handleChange("pickupStopId")}
                      displayEmpty
                      disabled={!formData.routeId || isStopDetailsLoading}
                      error={!!errors.pickupStopId}
                      data-field="pickupStopId"
                    >
                      <MenuItem value="" disabled>
                        {t("Select Pickup")}
                      </MenuItem>
                      {availablePickupStops.map((stop) => (
                        <MenuItem key={stop.id} value={stop.id}>
                          {stop.name}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </FormControl>
                  {errors.pickupStopId && (
                    <ErrorText>{errors.pickupStopId}</ErrorText>
                  )}
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="body2" sx={labelSx}>
                    {t("Drop Point")}
                    <Box component="span" sx={REQUIRED_ASTERISK_SX}>
                      *
                    </Box>
                  </Typography>
                  <FormControl fullWidth>
                    <StyledSelect
                      value={formData.dropStopId}
                      onChange={handleChange("dropStopId")}
                      displayEmpty
                      disabled={!formData.pickupStopId || isStopDetailsLoading}
                      error={!!errors.dropStopId}
                      data-field="dropStopId"
                    >
                      <MenuItem value="" disabled>
                        {t("Select Drop")}
                      </MenuItem>
                      {availableDropStops.map((stop) => (
                        <MenuItem key={stop.id} value={stop.id}>
                          {stop.name}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </FormControl>
                  {errors.dropStopId && (
                    <ErrorText>{errors.dropStopId}</ErrorText>
                  )}
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={labelSx}>
                    {t("Driver")}
                  </Typography>
                  <StyledTextField
                    fullWidth
                    value={formData.driverName || ""}
                    placeholder={t("Driver")}
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={labelSx}>
                    {t("Attendant")}
                  </Typography>
                  <StyledTextField
                    fullWidth
                    value={formData.attendantName || ""}
                    placeholder={t("Attendant")}
                    disabled
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={labelSx}>
                {t("NFC Card")}
              </Typography>
              <FormControl fullWidth>
                <StyledSelect
                  value={formData.nfcUid || ""}
                  onChange={handleChange("nfcUid")}
                  displayEmpty
                  data-field="nfcUid"
                >
                  <MenuItem value="">
                    {t("No card")}
                  </MenuItem>
                  {(formData.nfcUid &&
                  !nfcCards.some(
                    (card) =>
                      String(card.deviceId || card.uid || "") ===
                      String(formData.nfcUid),
                  )
                    ? [{ deviceId: formData.nfcUid }, ...nfcCards]
                    : nfcCards
                  ).map((card) => {
                    const uid = card.deviceId || card.uid;
                    return (
                      <MenuItem key={uid} value={uid}>
                        {uid}
                      </MenuItem>
                    );
                  })}
                </StyledSelect>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          md={4}
          sx={{ display: "flex", flexDirection: "column", gap: "8px" }}
        >
          <Button
            onClick={handleParentView}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              px: 2,
              py: 1,
              maxWidth: "200px",
              alignSelf: "end",
              fontWeight: 600,
              backgroundColor: "#F2F2F3",
              border: "2px solid #20A8E0",
              color: "#20A8E0",
              "&:hover": {
                backgroundColor: "#4fbceb",
                borderColor: "#20A8E0",
                color: "#FFFFFF",
              },
              height: "44px",
              padding: "16px 24px",
              fontSize: "16px",
            }}
          >
            {t("Parent view")}
          </Button>
          <MapContainer>
            {isStopDetailsLoading && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  backgroundColor: "rgba(255,255,255,0.45)",
                }}
              >
                {/* <CircularProgress size={1} thickness={1} /> */}
                <Typography variant="caption" sx={{ color: "#445164" }}>
                  {t("Loading route stops...")}
                </Typography>
              </Box>
            )}
            <GoogleMap
              mapContainerStyle={fullContainerStyle}
              center={BANGALORE_AIRPORT}
              zoom={12}
              onLoad={onLoad}
              onUnmount={onUnmount}
              options={getMapOptions({
                disableDefaultUI: false,
                zoomControl: true,
                mapTypeControl: true,
                scaleControl: true,
                streetViewControl: true,
                rotateControl: true,
                fullscreenControl: true,
              })}
            >
              {placeMarkers.map((place) => {
                const config = getPlaceConfig(place.type);
                if (!config?.icon) return null;

                return (
                  <Marker
                    key={place.id}
                    position={place.position}
                    title={place.name}
                    icon={{
                      url: config.icon,
                      scaledSize: new window.google.maps.Size(32, 32),
                    }}
                  />
                );
              })}

              {directions && (
                <DirectionsRenderer
                  directions={directions}
                  options={{
                    suppressMarkers: true,
                    preserveViewport: true,
                    polylineOptions: {
                      strokeColor: selectedRouteColor,
                      strokeOpacity: 0.8,
                      strokeWeight: 5,
                    },
                  }}
                />
              )}

              {stopsToDisplay.map((stop, index) => {
                const lat = stop.latitude;
                const lng = stop.longitude;
                if (isNaN(lat) || isNaN(lng)) return null;

                const isOrigin = index === 0;
                const isDestination = index === stopsToDisplay.length - 1;
                const stopIcon = isOrigin
                  ? getOriginStopIcon()
                  : isDestination
                    ? getDestinationStopIcon()
                    : getIntermediateStopIcon();

                return (
                  <React.Fragment key={`${stop.id}-${index}`}>
                    <OverlayView
                      position={{ lat, lng }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div
                        style={{
                          position: "relative",
                          top: isOrigin || isDestination ? "-73px" : "-40px",
                          left: "0px",
                        }}
                      >
                        <div
                          style={{
                            position: "absolute",
                            left: "50%",
                            transform: "translateX(-50%)",
                            backgroundColor:
                              isOrigin || isDestination
                                ? isOrigin
                                  ? "#20A8E0"
                                  : "#EE3B2B"
                                : "#FFFFFF",
                            color:
                              isOrigin || isDestination ? "#FFFFFF" : "#000000",
                            padding: "6px 10px",
                            borderRadius: "6px",
                            fontSize: "13px",
                            fontWeight: "600",
                            whiteSpace: "nowrap",
                            zIndex: 1000,
                            border:
                              isOrigin || isDestination
                                ? isOrigin
                                  ? "0.5px solid #20A8E0"
                                  : "0.5px solid #EE3B2B"
                                : "0.5px solid #000000",
                            maxWidth: "200px",
                            textAlign: "center",
                          }}
                        >
                          {stop.name}
                        </div>
                      </div>
                    </OverlayView>
                    <Marker position={{ lat, lng }} icon={stopIcon} />
                  </React.Fragment>
                );
              })}
            </GoogleMap>
          </MapContainer>
        </Grid>
      </Grid>

      {submitError && (
        <Box mt={3}>
          <ErrorText sx={{ textAlign: "center" }}>{submitError}</ErrorText>
        </Box>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={submitError ? 1 : 3}
      >
        <Box>
          {isEdit && onDelete && (
            <DeleteButton
              onClick={handleDeleteClick}
              startIcon={<DeleteIcon />}
            >
              {t("Delete")}
            </DeleteButton>
          )}
        </Box>
        <Box display="flex" gap="64px">
          {!isEdit && <CancelButton onClick={onClose}>{t("Cancel")}</CancelButton>}
          <ConfirmButton onClick={handleSubmit}>
            {isEdit ? t("Update") : t("Confirm")}
          </ConfirmButton>
        </Box>
        <Box></Box>
      </Box>

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("Delete Student!")}
        message={t(
          "Are you sure you want to delete this student? This action cannot be undone.",
        )}
        cancelButtonText={t("Cancel")}
        confirmButtonText={t("Delete")}
      />
    </FormContainer>
  );
}
